import { DateRangeGranularity } from 'components/DateFilter/utilities'
import { buildTranslate } from 'locales'
import moment, { Moment } from 'moment-timezone'

const t = buildTranslate('dateFormat')

export const getFormattedDate = (
  dateTime: Moment,
  granularity: DateRangeGranularity
): string => {
  switch (granularity) {
    case 'anniversary':
      return dateTime.format('MMM D')
    case 'daily':
      return dateTime.format('MMM D, YYYY')
    case 'weekly':
      return t('weekOf', {
        date: dateTime.format('MMM D, YYYY'),
      })
    case 'monthly':
      return t('monthYear', {
        month: dateTime.format('MMM'),
        year: dateTime.format('YYYY'),
      })
    case 'quarterly':
      return t('quarterYear', {
        quarter: dateTime.format('Q'),
        year: dateTime.format('YYYY'),
      })
    case 'yearly':
      return dateTime.format('YYYY')
  }
}

export const getDateLabel = (
  dateTime: string,
  granularity: DateRangeGranularity,
  timeZone: string
): string => {
  const dateMoment = getMomentFromSeriesCategory(
    dateTime,
    granularity,
    timeZone
  )
  switch (granularity) {
    case 'quarterly':
      return getFormattedDate(dateMoment, 'quarterly')
    case 'monthly': {
      const [year] = dateTime.split('-')
      return t('monthYear', { month: dateMoment.format('MMM'), year: year })
    }
    case 'weekly':
    case 'anniversary':
    case 'daily': {
      const [, , day] = dateTime.split('-')
      return !!day
        ? t('monthDay', {
            month: dateMoment.format('MMM'),
            day: Number(day),
          })
        : ''
    }
    case 'yearly':
      return dateTime
  }
}

export const getMomentFromSeriesCategory = (
  date: string,
  granularity: DateRangeGranularity,
  timeZone: string
): Moment => {
  switch (granularity) {
    case 'anniversary':
    case 'daily':
    case 'weekly':
      return moment.tz(date, timeZone)
    case 'monthly':
    case 'quarterly':
      return moment.tz(`${date}-01`, timeZone)
    case 'yearly':
      return moment.tz(`${date}-01-01`, timeZone)
  }
}

export const getDateWithTimeAndZone = (
  date: string,
  timeZone: string
): string => {
  const momentDate = moment.tz(date, timeZone)
  const datePart = momentDate.format('MMM D, YYYY')
  const timePart = momentDate.format('hh:mma z')
  return `${datePart} at ${timePart}`
}

// Time of day plus a zone abbreviation ("2:14 PM PDT"), with no date part — for
// timestamps recent enough that the day is unambiguous.
//
// `timeZone` defaults to the browser's, which is what callers displaying a
// "just happened" time want. It stays injectable so tests can pin a zone
// instead of depending on the machine they run on.
//
// Must go through `moment.tz`, not plain `moment`: moment-timezone formats the
// `z` token as an EMPTY STRING on a zone-unaware moment, which would silently
// render "2:14 PM " with a dangling space rather than failing loudly.
//
// Note the tokens differ from getDateWithTimeAndZone's `hh:mma` ('02:14pm') —
// this follows the design's `h:mm A` ('2:14 PM'). Worth reconciling if a single
// house time format is ever settled on.
export const getTimeWithZone = (
  date: string,
  timeZone: string = moment.tz.guess()
): string => moment.tz(date, timeZone).format('h:mm A z')

// Full-date timestamp with zone: "June 8, 2026 2:47PM PDT" (MMMM D, YYYY h:mmA z).
// A third house format alongside getDateWithTimeAndZone / getTimeWithZone — worth
// reconciling if one house format is ever settled on. Must use moment.tz, not plain
// moment: the `z` token renders empty on a zone-unaware moment (see above).
export const getLongDateWithTimeAndZone = (
  date: string,
  timeZone: string
): string => moment.tz(date, timeZone).format('MMMM D, YYYY h:mmA z')

// Elapsed time as a formed phrase ("2 days ago", "a day ago"). `from` defaults
// to now and stays injectable so tests can pin an interval rather than race the
// clock. Zone-independent — it is a difference between two instants.
export const getRelativeTime = (
  date: string,
  from?: Moment | Date | number
): string => (from ? moment(date).from(from) : moment(date).fromNow())
